import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import type { Dispatch } from '@reduxjs/toolkit';

import type { LiveStreamPreferences } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { Icon, IconSize } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import {
  closeLiveStream,
  selectStreamWithAutoplay,
  setPipMode,
  setStoredPreferences,
  toggleMutedState,
} from 'app/modules/LiveStream/actions';
import {
  selectedStream,
  selectLiveStreamEntriesFirst,
  selectLiveStreamPreferences,
} from 'app/modules/LiveStream/selectors';
import { openLoginDialog } from 'app/modules/Signing/actions';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { LiveStreamComponent } from '../../constants';
import { getPreferencesStorage, setPreferencesStorage } from '../../local-storage-helpers';
import type { RecordOfLiveStreamEntry } from '../../records';
import ToolbarButtons from '../ToolbarButtons';

import dictionary from './ToolbarControls.dictionary.json';

import styles from '../../LiveStream.module.scss';

type DispatchProp = {
  dispatch: Dispatch;
};

type VideoContainerProps = {
  videoContainer: HTMLDivElement | null;
};

type ToolbarControlsProps = {
  isLoggedIn: boolean;
  firstEntry: RecordOfLiveStreamEntry;
  playingStream: RecordOfLiveStreamEntry | null;
  liveStreamPreferences: LiveStreamPreferences;
  streamType: LiveStreamComponent | null;
};

const LiveStream: FC<ToolbarControlsProps & DispatchProp & VideoContainerProps> = ({
  videoContainer,
  firstEntry,
  isLoggedIn,
  playingStream,
  liveStreamPreferences,
  streamType,
  dispatch,
}) => {
  const [pipModeSupported, setPipModeSupported] = useState(false);
  const { closed, muted, streamId, pipMode } = liveStreamPreferences;
  const { t } = useTranslation();

  const setPipModeInListener = () => {
    dispatch(setPipMode(false));
  };

  useEffect(() => {
    if (!closed && streamId) {
      dispatch(selectStreamWithAutoplay(streamId));
    }

    if (isLoggedIn) {
      const preferences = getPreferencesStorage();

      preferences && dispatch(setStoredPreferences(preferences));
    }
  }, []);

  useEffect(() => {
    isLoggedIn && setPreferencesStorage(liveStreamPreferences);
  }, [liveStreamPreferences]);

  useEffect(() => {
    const isSupportPipMode = streamType === LiveStreamComponent.HLS;

    setPipModeSupported(isSupportPipMode);
  }, [playingStream?.matchId, streamType]);

  useEffect(() => {
    videoContainer && videoContainer.addEventListener('leavepictureinpicture', setPipModeInListener);

    return () => {
      videoContainer && videoContainer.removeEventListener('leavepictureinpicture', setPipModeInListener);
    };
  }, [videoContainer]);

  const handleOpen = useCallback(() => {
    if (!isLoggedIn) {
      dispatch(openLoginDialog());
    }
    else {
      const stream = playingStream ? playingStream.matchId : firstEntry.matchId;

      typeof stream === 'number' && dispatch(selectStreamWithAutoplay(stream));
    }
  }, [isLoggedIn, playingStream, firstEntry]);

  const handleClose = useCallback(() => {
    dispatch(closeLiveStream());

    if (document.pictureInPictureElement) {
      document.exitPictureInPicture()
        .catch((err) => console.error('LiveStream exit PiP mode error: ', err));

      dispatch(setPipMode(false));
    }
  }, [pipMode]);

  const toggleMuted = useCallback(() => {
    dispatch(toggleMutedState(!muted));
  }, [muted]);

  const togglePictureInPicture = useCallback(() => {
    const video = videoContainer ? videoContainer.querySelector('video') : null;

    if (document.pictureInPictureElement) {
      document.exitPictureInPicture()
        .catch((err) => console.error('LiveStream exit PiP mode error: ', err));
    }
    else {
      video && video.requestPictureInPicture()
        .catch((err) => console.error('LiveStream request PiP mode error: ', err));
    }

    dispatch(setPipMode(!pipMode));
  }, [pipMode, videoContainer]);

  const pipButtonClassNames = classNames({
    [styles.pipButtonHidden]: !('pictureInPictureEnabled' in document) || !pipModeSupported,
  });

  if (closed || !playingStream || !isLoggedIn) {
    return (
      <button type="button"
              title={isLoggedIn ? t(dictionary.play) : t(dictionary.notLogin)}
              data-ga-event="right-broadcastToggle"
              data-ga-event-value="On"
              onClick={handleOpen}>
        <Icon icon="play"
              size={IconSize.MEDIUM} />
      </button>
    );
  }

  return (
    <ToolbarButtons muted={muted}
                    pipMode={pipMode}
                    handleClose={handleClose}
                    toggleMuted={toggleMuted}
                    togglePictureInPicture={togglePictureInPicture}
                    pipButtonClassNames={pipButtonClassNames} />
  );
};

const mapStateToProps = (state: ReduxState): ToolbarControlsProps => {
  const playingStream = selectedStream(state) ?? null;
  const streamType = playingStream?.type || null;

  return ({
    isLoggedIn: selectIsLoggedIn(state),
    liveStreamPreferences: selectLiveStreamPreferences(state),
    firstEntry: selectLiveStreamEntriesFirst(state),
    playingStream,
    streamType,
  });
};

export default connect(mapStateToProps)(LiveStream);
